import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Button,
    alpha,
    useTheme,
    Tabs,
    Tab,
    IconButton,
    Chip,
    Avatar,
    Tooltip,
    Skeleton,
    Rating,
    Grid,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import {
    LocalShipping as LogisticsIcon,
    Business as SuppliersIcon,
    CompareArrows as RateShoppingIcon,
    Assessment as PerformanceIcon,
    Add as AddIcon,
    Refresh as RefreshIcon,
    FilterList as FilterIcon,
    MoreVert as MoreIcon,
    Star as StarIcon,
    Public as GlobalIcon,
    CheckCircle as ActiveIcon,
    TrendingDown as CostIcon,
    Speed as VelocityIcon,
    Warning as AlertIcon,
    Email as ContactIcon,
    Phone as PhoneIcon,
    Security as SecurityIcon,
    Edit as EditIcon, Traffic as TrafficIcon, AltRoute as RouteIcon,
    ViewKanban as KanbanIcon,
    TableChart as TableIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import PageWrapper from '../../components/common/PageWrapper';
import StatsCard, { StatsCardGrid } from '../../components/common/StatsCard';
import SmartDataGrid from '../../components/common/SmartDataGrid';
import GlassPanel from '../../components/common/GlassPanel';
import KanbanView from '../../components/ui/KanbanView';
import { useSuppliers, useCreateSupplier, useUpdateSupplier } from '../../hooks/useSuppliers';
import {
    useCarriers,
    useShippingStats,
    useCarrierPerformance,
    useRateQuotes,
    useCreateCarrier,
    useUpdateCarrier
} from '../../hooks/useShipping';
import { useSnackbar } from 'notistack';
import WarehouseTrafficMap from '../../components/tms/WarehouseTrafficMap';
import OrderRoutingControl from '../../components/tms/OrderRoutingControl';
import {
  SUPPORTED_CURRENCIES,
  getCurrencySymbol,
} from '../../utils/invoice-pdf-general-section-utilities';

const SUPPLIER_STATUS_KANBAN_COLUMNS = [
    { id: 'active', label: 'Active', color: '#01B574' },
    { id: 'pending', label: 'Pending', color: '#FFB547' },
    { id: 'inactive', label: 'Inactive', color: '#EE5D50' }
];

const CARRIER_STATUS_KANBAN_COLUMNS = [
    { id: 'active', label: 'Active', color: '#01B574' },
    { id: 'inactive', label: 'Inactive', color: '#EE5D50' }
];

/**
 * LogisticsCockpitPage - High-fidelity Supply Chain & Logistics Orchestration Center
 * Aurora 2026 Design Standard
 */
const LogisticsCockpitPage = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();

    // Tab Management (handle query param tab=N)
    const queryParams = new URLSearchParams(location.search);
    const initialTab = parseInt(queryParams.get('tab') || '0', 10);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const [onboardOpen, setOnboardOpen] = useState(false);
    const [partnerForm, setPartnerForm] = useState({ name: '', type: 'supplier', contact: '', email: '' });
    const [viewMode, setViewMode] = useState('table');
    const [selectedCurrency, setSelectedCurrency] = useState('EUR');
    const { enqueueSnackbar } = useSnackbar();

    const formatAmount = useCallback((amount) => {
        const symbol = getCurrencySymbol(selectedCurrency);
        return new Intl.NumberFormat(i18n?.language || 'en', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount || 0).replace(/^/, symbol);
    }, [selectedCurrency, i18n?.language]);

    // Data Fetching
    const { data: suppliersData, isLoading: suppliersLoading, refetch: refetchSuppliers } = useSuppliers();
    const { data: carriersData, isLoading: carriersLoading, refetch: refetchCarriers } = useCarriers();

    // Mutation hooks for creating/updating partners
    const createSupplier = useCreateSupplier();
    const updateSupplier = useUpdateSupplier();
    const createCarrier = useCreateCarrier();
    const updateCarrier = useUpdateCarrier();

    const handleOnboardSubmit = async () => {
        if (!partnerForm.name) return;
        
        try {
            if (partnerForm.type === 'supplier') {
                await createSupplier.mutateAsync({
                    name: partnerForm.name,
                    contact_name: partnerForm.contact,
                    email: partnerForm.email,
                    status: 'active'
                });
            } else {
                await createCarrier.mutateAsync({
                    name: partnerForm.name,
                    contact_name: partnerForm.contact,
                    email: partnerForm.email,
                    is_active: true
                });
            }
            setOnboardOpen(false);
            setPartnerForm({ name: '', type: 'supplier', contact: '', email: '' });
        } catch (err) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Error creating partner:', err);
            }
        }
    };

    const { data: statsData, isLoading: statsLoading } = useShippingStats();
    const { data: perfData, isLoading: perfLoading } = useCarrierPerformance();

    const isLoading = suppliersLoading || carriersLoading || statsLoading || perfLoading;

    // Memoized Data
    const suppliers = useMemo(() => Array.isArray(suppliersData) ? suppliersData : (suppliersData?.suppliers || []), [suppliersData]);
    const carriers = useMemo(() => Array.isArray(carriersData) ? carriersData : (carriersData?.carriers || []), [carriersData]);
    const performance = useMemo(() => perfData || {}, [perfData]);

    // Transform suppliers data for KanbanView
    const kanbanSuppliers = useMemo(() => {
        return suppliers.map(s => ({
            id: s.id,
            title: s.name || `Supplier #${s.id}`,
            subtitle: `${s.contact_name || 'No contact'} | ${s.city || 'No location'}`,
            status: s.status || 'active',
            metadata: {
                category: s.category,
                rating: s.rating,
                email: s.email
            }
        }));
    }, [suppliers]);

    // Transform carriers data for KanbanView
    const kanbanCarriers = useMemo(() => {
        return carriers.map(c => ({
            id: c.id,
            title: c.name || `Carrier #${c.id}`,
            subtitle: `${c.carrier_type || 'Unknown type'} | ${c.scac || 'No SCAC'}`,
            status: c.is_active ? 'active' : 'inactive',
            metadata: {
                carrier_type: c.carrier_type,
                scac: c.scac,
                onsite_performance: c.onsite_performance
            }
        }));
    }, [carriers]);

    const handleViewModeChange = useCallback((event, newMode) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    }, []);

    const handleSupplierStatusChange = useCallback(async (supplierId, newStatus) => {
        try {
            await updateSupplier.mutateAsync({ supplierId, data: { status: newStatus } });
            enqueueSnackbar(t('suppliers.statusUpdated', 'Supplier status updated to {{status}}', { status: newStatus }), { variant: 'success' });
            refetchSuppliers();
        } catch (err) {
            enqueueSnackbar(t('suppliers.statusUpdateError', 'Error updating supplier status'), { variant: 'error' });
        }
    }, [updateSupplier, enqueueSnackbar, t, refetchSuppliers]);

    const handleCarrierStatusChange = useCallback(async (carrierId, newStatus) => {
        try {
            const isActive = newStatus === 'active';
            await updateCarrier.mutateAsync({ carrierId, data: { is_active: isActive } });
            enqueueSnackbar(t('carriers.statusUpdated', 'Carrier status updated to {{status}}', { status: newStatus }), { variant: 'success' });
            refetchCarriers();
        } catch (err) {
            enqueueSnackbar(t('carriers.statusUpdateError', 'Error updating carrier status'), { variant: 'error' });
        }
    }, [updateCarrier, enqueueSnackbar, t, refetchCarriers]);

    // Tab Handlers
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setSelectedItem(null);
        setIsSidebarOpen(false);
        navigate(`/admin/logistics?tab=${newValue}`, { replace: true });
    };

    // Supplier Columns
    const supplierColumns = [
        { field: 'id', headerName: 'ID', width: 90 },
        {
            field: 'name',
            headerName: t('suppliers.name', 'Supplier Name'),
            width: 250,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, fontSize: '0.85rem' }}>
                        {params.value?.[0]}
                    </Avatar>
                    <Box>
                        <Typography variant="body2" fontWeight="medium">{params.value}</Typography>
                        <Typography variant="caption" color="text.secondary">{params.row.category || '-'}</Typography>
                    </Box>
                </Box>
            )
        },
        {
            field: 'status',
            headerName: t('common.status', 'Status'),
            width: 120,
            renderCell: (params) => (
                <Chip
                    label={params.value || 'active'}
                    size="small"
                    color={params.value === 'active' ? 'success' : 'warning'}
                    variant="outlined"
                />
            )
        },
        {
            field: 'rating',
            headerName: t('suppliers.rating', 'Rating'),
            width: 150,
            renderCell: (params) => (
                <Rating value={params.value || 0} readOnly size="small" precision={0.5} />
            )
        },
        { field: 'contact_name', headerName: t('common.contact', 'Contact'), width: 180 },
        { field: 'city', headerName: t('common.city', 'City'), width: 130 }
    ];

    // Carrier Columns
    const carrierColumns = [
        { field: 'id', headerName: 'ID', width: 90 },
        {
            field: 'name',
            headerName: t('carriers.name', 'Carrier'),
            width: 200,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 0.5, borderRadius: 1, bgcolor: alpha(theme.palette.secondary.main, 0.1) }}>
                        <LogisticsIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
                    </Box>
                    <Typography variant="body2">{params.value}</Typography>
                </Box>
            )
        },
        {
            field: 'is_active',
            headerName: t('common.active', 'Active'),
            width: 100,
            renderCell: (params) => (
                <ActiveIcon color={params.value ? 'success' : 'disabled'} sx={{ fontSize: 20 }} />
            )
        },
        {
            field: 'onsite_performance',
            headerName: t('carriers.performance', 'On-Time %'),
            width: 150,
            renderCell: (params) => (
                <Box sx={{ width: '100%', pr: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">{params.value || 92}%</Typography>
                    </Box>
                    <Box sx={{ height: 4, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2 }}>
                        <Box sx={{ width: `${params.value || 92}%`, height: '100%', bgcolor: theme.palette.success.main, borderRadius: 2 }} />
                    </Box>
                </Box>
            )
        },
        { field: 'carrier_type', headerName: t('carriers.type', 'Type'), width: 150 },
        { field: 'scac', headerName: 'SCAC', width: 100 }
    ];

    return (
        <PageWrapper
            title={t('logistics.cockpit.title', 'Supply Chain & Logistics Cockpit')}
            subtitle={t('logistics.cockpit.subtitle', 'Strategic management of suppliers, carriers and shipping performance')}
            breadcrumbs={[
                { label: t('nav.admin', 'Admin'), path: '/admin' },
                { label: t('logistics.cockpit.title', 'Logistics Cockpit'), icon: LogisticsIcon }
            ]}
            actions={
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => {
                            refetchSuppliers();
                            refetchCarriers();
                        }}
                    >
                        {t('common.refresh', 'Refresh')}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOnboardOpen(true)}
                    >
                        {t('logistics.onboard', 'Onboard Partner')}
                    </Button>
                </Box>
            }
        >
            {/* KPI Cards Section */}
            <Box sx={{ mb: 4 }}>
                <StatsCardGrid columns={4}>
                    <StatsCard
                        icon={SuppliersIcon}
                        title={t('logistics.activeSuppliers', 'Active Suppliers')}
                        value={isLoading ? <Skeleton width={40} /> : statsData?.active_suppliers || suppliers.filter(s => s.status === 'active').length}
                        trend="+2 this month"
                        color={theme.palette.primary.main}
                    />
                    <StatsCard
                        icon={VelocityIcon}
                        title={t('logistics.onTimeRate', 'Avg On-Time Rate')}
                        value={isLoading ? <Skeleton width={60} /> : statsData?.onTimeDeliveryRate || t('common.noData', '0%')}
                        trendColor="success"
                        color={theme.palette.success.main}
                    />
                    <StatsCard
                        icon={AlertIcon}
                        title={t('logistics.exceptions', 'Shipping Exceptions')}
                        value={isLoading ? <Skeleton width={40} /> : statsData?.exceptions || 0}
                        trend={statsData?.exceptions > 0 ? t('logistics.needsAttention', 'Needs Attention') : t('common.noData', 'No data')}
                        trendColor="error"
                        color={theme.palette.error.main}
                    />
                    <StatsCard
                        icon={CostIcon}
                        title={t('logistics.monthlyCost', 'Logistics Cost (MTD)')}
                        value={isLoading ? <Skeleton width={80} /> : statsData?.totalShippingCost ? formatAmount(statsData.totalShippingCost) : formatAmount(0)}
                        subtitle={t('logistics.strategicSpend', 'Strategic shipping spend')}
                        color={theme.palette.info.main}
                    />
                </StatsCardGrid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>{t('common.currency', 'Currency')}</InputLabel>
                        <Select
                            value={selectedCurrency}
                            label={t('common.currency', 'Currency')}
                            onChange={(e) => setSelectedCurrency(e.target.value)}
                        >
                            {SUPPORTED_CURRENCIES.map((currency) => (
                                <MenuItem key={currency} value={currency}>
                                    {getCurrencySymbol(currency)} {currency}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* Main Cockpit Interface */}
            <GlassPanel sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 400px)', minHeight: 650 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Tabs value={activeTab} onChange={handleTabChange} sx={{ minHeight: 64 }}>
                        <Tab
                            icon={<SuppliersIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('logistics.tabs.suppliers', 'Suppliers')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<LogisticsIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('logistics.tabs.carriers', 'Carriers')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<RateShoppingIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('logistics.tabs.rateShopping', 'Rate Shopping')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<PerformanceIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('logistics.tabs.performance', 'Logistics Analytics')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<TrafficIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('logistics.tabs.traffic', 'Internal Traffic')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<RouteIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('logistics.tabs.routing', 'Order Strategy')}
                            sx={{ minHeight: 64 }}
                        />
                    </Tabs>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {(activeTab === 0 || activeTab === 1) && (
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={handleViewModeChange}
                                size="small"
                            >
                                <ToggleButton value="table" aria-label="Table view">
                                    <Tooltip title={t('common.tableView', 'Table View')}>
                                        <TableIcon />
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton value="kanban" aria-label="Kanban view">
                                    <Tooltip title={t('common.kanbanView', 'Kanban View')}>
                                        <KanbanIcon />
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        )}
                        <Tooltip title={t('common.partnerMap', 'Partner Map')}>
                            <IconButton size="small"><GlobalIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.filterView', 'Filter View')}>
                            <IconButton size="small"><FilterIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.settings', 'Settings')}>
                            <IconButton size="small"><MoreIcon fontSize="small" /></IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                    <Box sx={{
                        display: 'flex',
                        width: '100%',
                        height: '100%',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        {/* Grid Area */}
                        <Box sx={{
                            flexGrow: 1,
                            height: '100%',
                            width: isSidebarOpen ? 'calc(100% - 420px)' : '100%',
                            transition: 'all 0.3s ease'
                        }}>
                            {activeTab === 0 && (
                                viewMode === 'kanban' ? (
                                    <KanbanView
                                        columns={SUPPLIER_STATUS_KANBAN_COLUMNS}
                                        items={kanbanSuppliers}
                                        onStatusChange={handleSupplierStatusChange}
                                        loading={suppliersLoading}
                                    />
                                ) : (
                                    <SmartDataGrid
                                        rows={suppliers}
                                        columns={supplierColumns}
                                        loading={suppliersLoading}
                                        onRowClick={(params) => {
                                            setSelectedItem({ ...params.row, _type: 'supplier' });
                                            setIsSidebarOpen(true);
                                        }}
                                    />
                                )
                            )}
                            {activeTab === 1 && (
                                viewMode === 'kanban' ? (
                                    <KanbanView
                                        columns={CARRIER_STATUS_KANBAN_COLUMNS}
                                        items={kanbanCarriers}
                                        onStatusChange={handleCarrierStatusChange}
                                        loading={carriersLoading}
                                    />
                                ) : (
                                    <SmartDataGrid
                                        rows={carriers}
                                        columns={carrierColumns}
                                        loading={carriersLoading}
                                        onRowClick={(params) => {
                                            setSelectedItem({ ...params.row, _type: 'carrier' });
                                            setIsSidebarOpen(true);
                                        }}
                                    />
                                )
                            )}
                            {activeTab === 2 && (
                                <Box sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                    <RateShoppingIcon sx={{ fontSize: 80, color: 'divider', mb: 2 }} />
                                    <Typography variant="h5" color="text.secondary">{t('common.logistics.rateShoppingHub', 'Integrated Rate Shopping Hub')}</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 500 }}>
                                        {t('common.logistics.rateShoppingDescription', 'Compare real-time rates across all authorized carriers for specific shipment parameters.')}
                                    </Typography>
                                    <Button variant="contained" sx={{ mt: 3 }} startIcon={<AddIcon />}>
                                        {t('common.logistics.launchRateComparison', 'Launch Rate Comparison')}
                                    </Button>
                                </Box>
                            )}
                            {activeTab === 3 && (
                                <Box sx={{ p: 4, height: '100%' }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={8}>
                                            <Typography variant="h6" gutterBottom>{t('common.logistics.carrierPerformanceMatrix', 'Carrier Performance Matrix')}</Typography>
                                            <Box sx={{ height: 350, bgcolor: alpha(theme.palette.primary.main, 0.03), borderRadius: 2, border: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Typography color="text.secondary">{t('common.logistics.performanceTrendVisualization', 'Performance Trend Visualization')}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Typography variant="h6" gutterBottom>{t('common.logistics.topCarriersByQuality', 'Top Carriers by Quality')}</Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {(carriersData?.items || carriersData || []).length > 0 ? (
                                                    (carriersData?.items || carriersData || [])
                                                        .slice()
                                                        .sort((a, b) => (b.rating || 0) - (a.rating || 0))
                                                        .slice(0, 3)
                                                        .map((carrier, i) => (
                                                            <Box key={carrier.id || i} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                    <Typography variant="subtitle2">{carrier.name || '-'}</Typography>
                                                                    <Typography variant="caption" color="success.main"># {i + 1}</Typography>
                                                                </Box>
                                                                <Rating value={carrier.rating || 0} readOnly size="small" />
                                                            </Box>
                                                        ))
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">{t('common.logistics.noCarrierData', 'No carrier data available')}</Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                            {activeTab === 4 && (
                                <Box sx={{ height: '100%', p: 2 }}>
                                    <WarehouseTrafficMap />
                                </Box>
                            )}
                            {activeTab === 5 && (
                                <Box sx={{ height: "100%", p: 2 }}>
                                    <OrderRoutingControl />
                                </Box>
                            )}

                        </Box>

                        {/* Glass Sidebar */}
                        {isSidebarOpen && selectedItem && (
                            <Box sx={{
                                width: 420,
                                borderLeft: '1px solid',
                                borderColor: 'divider',
                                bgcolor: alpha(theme.palette.background.paper, 0.8),
                                backdropFilter: 'blur(20px)',
                                p: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                animation: 'slideIn 0.3s ease-out',
                                overflowY: 'auto'
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {selectedItem._type === 'supplier' ? <SuppliersIcon /> : <LogisticsIcon />}
                                        {t('common.logistics.partnerOrchestration', 'Partner Orchestration')}
                                    </Typography>
                                    <IconButton onClick={() => setIsSidebarOpen(false)} size="small">
                                        <MoreIcon sx={{ transform: 'rotate(90deg)' }} />
                                    </IconButton>
                                </Box>

                                <Box sx={{ mb: 4 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                        <Avatar sx={{ width: 64, height: 64, fontSize: '1.5rem', bgcolor: theme.palette.primary.main }}>
                                            {selectedItem.name?.[0]}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h5">{selectedItem.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{t('common.logistics.partnerId', 'Partner ID')}: {selectedItem.id}</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                        <Chip label={selectedItem.status || '-'} color="success" size="small" />
                                        <Chip label={selectedItem.category || '-'} variant="outlined" size="small" />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    <Box>
                                        <Typography variant="overline" color="text.secondary">{t('common.logistics.contactInformation', 'Contact Information')}</Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <ContactIcon fontSize="small" color="action" />
                                                <Typography variant="body2">{selectedItem.contact_name || '-'}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <ContactIcon fontSize="small" color="action" />
                                                <Typography variant="body2">{selectedItem.email || '-'}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <PhoneIcon fontSize="small" color="action" />
                                                <Typography variant="body2">{selectedItem.phone || '-'}</Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Typography variant="overline" color="text.secondary">{t('common.logistics.partnerPerformance', 'Partner Performance')}</Typography>
                                        <Box sx={{ p: 2, mt: 1, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                                <Typography variant="body2">{t('common.logistics.qualityScore', 'Quality Score')}</Typography>
                                                <Rating value={selectedItem.rating || 4.5} readOnly size="small" />
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">{t('common.logistics.commercialTerms', 'Commercial Terms')}</Typography>
                                                <Typography variant="body2" fontWeight="bold">{selectedItem.payment_terms || t('common.logistics.net30', 'Net 30')}</Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Typography variant="overline" color="text.secondary">{t('common.logistics.governanceCompliance', 'Governance & Compliance')}</Typography>
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                            <Tooltip title={t('common.logistics.certifiedGlobalPartner', 'Certified Global Partner')}>
                                                <SecurityIcon color="primary" />
                                            </Tooltip>
                                            <Typography variant="body2">{t('common.logistics.isoCompliant', 'ISO 9001, GDP Compliant')}</Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                                        <Button fullWidth variant="contained" startIcon={<EditIcon />}>
                                            {t('common.logistics.editConfiguration', 'Edit Configuration')}
                                        </Button>
                                        <Button variant="outlined" color="error">
                                            {t('common.logistics.deactivate', 'Deactivate')}
                                        </Button>
                                    </Box>
                                </Box>

                                <Box sx={{ mt: 'auto', pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Button fullWidth variant="text">
                                        {t('common.logistics.viewAuditTrail', 'View Strategic Audit Trail')}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </GlassPanel>


            <Dialog open={onboardOpen} onClose={() => setOnboardOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('common.logistics.onboardNewPartner', 'Onboard New Partner')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField
                            label={t('common.logistics.partnerName', 'Partner Name')}
                            fullWidth
                            value={partnerForm.name}
                            onChange={(e) => setPartnerForm({ ...partnerForm, name: e.target.value })}
                        />
                        <FormControl fullWidth>
                            <InputLabel>{t('common.logistics.partnerType', 'Partner Type')}</InputLabel>
                            <Select
                                value={partnerForm.type}
                                label={t('common.logistics.partnerType', 'Partner Type')}
                                onChange={(e) => setPartnerForm({ ...partnerForm, type: e.target.value })}
                            >
                                <MenuItem value="supplier">{t('common.logistics.supplier', 'Supplier')}</MenuItem>
                                <MenuItem value="carrier">{t('common.logistics.logisticsCarrier', 'Logistics Carrier')}</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label={t('common.logistics.contactPerson', 'Contact Person')}
                            fullWidth
                            value={partnerForm.contact}
                            onChange={(e) => setPartnerForm({ ...partnerForm, contact: e.target.value })}
                        />
                        <TextField
                            label={t('common.logistics.emailAddress', 'Email Address')}
                            fullWidth
                            value={partnerForm.email}
                            onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOnboardOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
                    <Button variant="contained" onClick={handleOnboardSubmit}>{t('common.logistics.onboardPartner', 'Onboard Partner')}</Button>
                </DialogActions>
            </Dialog>

            <style>
                {`
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}
            </style>
        </PageWrapper>
    );
};

export default React.memo(LogisticsCockpitPage);
